import * as React from 'react';
import {
  Box,
  Container,
  Button,
  Typography,
  Avatar,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  CircularProgress,
  Tooltip,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Hotel as HotelIcon,
  Bed as BedIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Formik, Form } from 'formik';
import { reservationSchema } from 'schema';
import { useNavigate } from 'react-router-dom';

import { useReservation } from './ReserveRoomComponents/useReservation';
import RoomDetailsStep from './ReserveRoomComponents/RoomDetailsStep';
import CustomerDetailsStep from './ReserveRoomComponents/CustomerDetailsStep';
import PaymentDetailsStep from './ReserveRoomComponents/PaymentDetailsStep';
import ReservationSummary from './ReserveRoomComponents/ReservationSummary';
import { STEPS, TOTAL_STEPS } from './ReserveRoomComponents/constants';
import {
  canAdvanceFromRoomStep,
  canAdvanceFromCustomerStep,
  canSubmitReservation,
  listValidationIssues
} from './ReserveRoomComponents/stepValidators';
import {
  GRADIENT_PRIMARY,
  GRADIENT_SUCCESS,
  GRADIENT_CONFIRM,
  SHADOW_CARD_LIFTED,
  SHADOW_FOOTER,
  COLOR_PAGE_BG,
  COLOR_BORDER_NEUTRAL,
  TRANSITION_DEFAULT
} from './ReserveRoomComponents/theme';

const STEP_DEFINITIONS = [
  { icon: <BedIcon />, label: 'Habitación', step: STEPS.ROOM },
  { icon: <PersonIcon />, label: 'Huésped', step: STEPS.CUSTOMER },
  { icon: <AttachMoneyIcon />, label: 'Pago', step: STEPS.PAYMENT }
];

const ReserveRoom = () => {
  const navigate = useNavigate();
  const reservation = useReservation();

  const {
    activeStep,
    initialValues,
    setInitialValues,
    selectedRoomData,
    amount,
    currency,
    exchangeRates,
    isSearching,
    setIsSearching,
    formSubmitting,
    splitPayments,
    setSplitPayments,
    showSplitPayments,
    setShowSplitPayments,
    existingCustomerData,
    hotel,
    fetchCustomerData,
    calculateAmount,
    handleTotalAmount,
    syncTotalAmount,
    syncForeignCurrencyAmount,
    handleAddSplitPayment,
    handleRemoveSplitPayment,
    handleUpdateSplitPayment,
    getTotalPaid,
    areSplitPaymentsValid,
    AddData,
    handleNext,
    handleBack,
    getProgress,
    stayTypes,
    promoCode,
    setPromoCode,
    discountAmount,
    finalAmount
  } = reservation;

  const handleSubmit = (values, { resetForm }) => {
    const submitValues = { ...values };
    if (showSplitPayments) {
      submitValues.splitPayments = splitPayments;
      submitValues.paymentMethod = 'split';
      submitValues.totalPaid = getTotalPaid();
    } else {
      submitValues.splitPayments = [];
    }
    AddData(submitValues, resetForm);
  };

  return (
    <Box sx={{ bgcolor: COLOR_PAGE_BG, minHeight: '100vh', pb: 4 }}>
      <Header onBack={() => navigate('/dashboard/roommanagement')} />

      <Container maxWidth="lg">
        <ProgressBar activeStep={activeStep} progress={getProgress()} />

        <Formik
          initialValues={initialValues}
          validationSchema={reservationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched, isValid }) => (
            <Form encType="multipart/form-data">
              {activeStep === STEPS.ROOM && (
                <RoomDetailsStep
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  selectedRoomData={selectedRoomData}
                  currency={currency}
                  amount={amount}
                  calculateAmount={calculateAmount}
                  setInitialValues={setInitialValues}
                  stayTypes={stayTypes}
                />
              )}

              {activeStep === STEPS.CUSTOMER && (
                <CustomerDetailsStep
                  values={values}
                  setFieldValue={setFieldValue}
                  isSearching={isSearching}
                  setIsSearching={setIsSearching}
                  fetchCustomerData={fetchCustomerData}
                  existingCustomerData={existingCustomerData}
                  hotel={hotel}
                />
              )}

              {activeStep === STEPS.PAYMENT && (
                <>
                  <PaymentDetailsStep
                    values={values}
                    setFieldValue={setFieldValue}
                    showSplitPayments={showSplitPayments}
                    setShowSplitPayments={setShowSplitPayments}
                    splitPayments={splitPayments}
                    setSplitPayments={setSplitPayments}
                    amount={amount}
                    currency={currency}
                    exchangeRates={exchangeRates}
                    handleTotalAmount={handleTotalAmount}
                    syncTotalAmount={syncTotalAmount}
                    syncForeignCurrencyAmount={syncForeignCurrencyAmount}
                    handleAddSplitPayment={handleAddSplitPayment}
                    handleRemoveSplitPayment={handleRemoveSplitPayment}
                    handleUpdateSplitPayment={handleUpdateSplitPayment}
                    getTotalPaid={getTotalPaid}
                    areSplitPaymentsValid={areSplitPaymentsValid}
                    promoCode={promoCode}
                    setPromoCode={setPromoCode}
                    discountAmount={discountAmount}
                    finalAmount={finalAmount}
                  />

                  <ReservationSummary
                    values={values}
                    amount={amount}
                    currency={currency}
                    showSplitPayments={showSplitPayments}
                    splitPayments={splitPayments}
                    getTotalPaid={getTotalPaid}
                    promoCode={promoCode}
                    discountAmount={discountAmount}
                    finalAmount={finalAmount}
                  />
                </>
              )}

              <NavigationButtons
                activeStep={activeStep}
                values={values}
                errors={errors}
                isValid={isValid}
                formSubmitting={formSubmitting}
                showSplitPayments={showSplitPayments}
                areSplitPaymentsValid={areSplitPaymentsValid}
                getTotalPaid={getTotalPaid}
                stayTypes={stayTypes}
                onBack={handleBack}
                onNext={handleNext}
              />
            </Form>
          )}
        </Formik>
      </Container>
    </Box>
  );
};

// ============================================================================
// Sub-componentes locales — solo presentación, sin lógica de negocio.
// ============================================================================

const Header = ({ onBack }) => (
  <Box
    sx={{
      background: GRADIENT_PRIMARY,
      color: 'white',
      py: 3,
      mb: 4,
      boxShadow: SHADOW_CARD_LIFTED
    }}
  >
    <Container maxWidth="lg">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          color: 'white',
          mb: 2,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
        }}
      >
        Volver a Gestión de Habitaciones
      </Button>
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
          <HotelIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Nueva Reserva
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Complete los datos del huésped y la reserva
          </Typography>
        </Box>
      </Box>
    </Container>
  </Box>
);

const ProgressBar = ({ activeStep, progress }) => (
  <Card sx={{ mb: 4, overflow: 'visible' }}>
    <CardContent sx={{ pb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Progreso de la reserva: {Math.round(progress)}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: COLOR_BORDER_NEUTRAL,
            '& .MuiLinearProgress-bar': {
              background: GRADIENT_SUCCESS,
              borderRadius: 4
            }
          }}
        />
      </Box>

      <Stack direction="row" spacing={2} justifyContent="space-between">
        {STEP_DEFINITIONS.map((item, index) => (
          <StepIndicator key={item.step} item={item} index={index} activeStep={activeStep} />
        ))}
      </Stack>
    </CardContent>
  </Card>
);

const StepIndicator = ({ item, index, activeStep }) => {
  const isActive = activeStep === item.step;
  const isReached = activeStep >= item.step;
  const isCompleted = activeStep > item.step;
  const isLast = index === TOTAL_STEPS - 1;

  return (
    <Box
      sx={{
        flex: 1,
        textAlign: 'center',
        position: 'relative',
        '&::after': !isLast
          ? {
              content: '""',
              position: 'absolute',
              top: '20px',
              right: '-16px',
              width: '32px',
              height: '2px',
              bgcolor: isCompleted ? 'primary.main' : COLOR_BORDER_NEUTRAL,
              zIndex: 0
            }
          : {}
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          margin: '0 auto',
          bgcolor: isReached ? 'primary.main' : COLOR_BORDER_NEUTRAL,
          color: isReached ? 'white' : 'text.secondary',
          transition: TRANSITION_DEFAULT,
          position: 'relative',
          zIndex: 1
        }}
      >
        {isCompleted ? <CheckCircleIcon /> : item.icon}
      </Avatar>
      <Typography
        variant="caption"
        display="block"
        sx={{
          mt: 1,
          fontWeight: isActive ? 'bold' : 'normal',
          color: isActive ? 'primary.main' : 'text.secondary'
        }}
      >
        {item.label}
      </Typography>
    </Box>
  );
};

const NavigationButtons = ({
  activeStep,
  values,
  errors,
  isValid,
  formSubmitting,
  showSplitPayments,
  areSplitPaymentsValid,
  getTotalPaid,
  stayTypes,
  onBack,
  onNext
}) => {
  const isFinalStep = activeStep === STEPS.PAYMENT;

  const nextDisabled =
    (activeStep === STEPS.ROOM && !canAdvanceFromRoomStep(values, stayTypes)) ||
    (activeStep === STEPS.CUSTOMER && !canAdvanceFromCustomerStep(values));

  // En el paso final, los errores de Yup pueden venir de pasos anteriores
  // (cédula, email, fecha de salida...). Los listamos para que el bloqueo
  // del botón nunca sea silencioso.
  const validationIssues = isFinalStep && !showSplitPayments ? listValidationIssues(errors) : [];

  return (
    <Card sx={{ p: 2, boxShadow: SHADOW_FOOTER, mt: 3 }}>
      {validationIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight="bold" display="block">
            Para confirmar la reserva, revise:
          </Typography>
          {validationIssues.map((issue) => (
            <Typography key={issue} variant="caption" display="block">
              • {issue}
            </Typography>
          ))}
        </Alert>
      )}
      <Box display="flex" justifyContent="space-between">
        <Button
          color="inherit"
          disabled={activeStep === STEPS.ROOM}
          onClick={onBack}
          size="large"
          startIcon={<ArrowBackIcon />}
        >
          Atrás
        </Button>

        {!isFinalStep ? (
          <Button
            variant="contained"
            onClick={onNext}
            size="large"
            disabled={nextDisabled}
            sx={{ px: 4, background: GRADIENT_PRIMARY }}
          >
            Siguiente Paso
          </Button>
        ) : (
          <SubmitButton
            values={values}
            isValid={isValid}
            formSubmitting={formSubmitting}
            showSplitPayments={showSplitPayments}
            areSplitPaymentsValid={areSplitPaymentsValid}
            getTotalPaid={getTotalPaid}
          />
        )}
      </Box>
    </Card>
  );
};

const SubmitButton = ({
  values,
  isValid,
  formSubmitting,
  showSplitPayments,
  areSplitPaymentsValid,
  getTotalPaid
}) => {
  const advanceNum = parseFloat(values.advanceAmount || 0);

  const tooltipMessage = (() => {
    if (formSubmitting) return 'Procesando...';
    if (showSplitPayments) {
      if (!values.advanceAmount || advanceNum <= 0) {
        return 'Debe ingresar un anticipo mayor a 0';
      }
      if (!areSplitPaymentsValid(values.advanceAmount)) {
        return `Distribuya los $${advanceNum.toFixed(2)} del anticipo. Total actual: $${getTotalPaid().toFixed(2)}`;
      }
      return 'Puede confirmar la reserva';
    }
    return isValid ? 'Puede confirmar la reserva' : 'Complete todos los campos requeridos';
  })();

  const disabled = !canSubmitReservation({
    values,
    isValid,
    formSubmitting,
    showSplitPayments,
    areSplitPaymentsValid
  });

  return (
    <Tooltip title={tooltipMessage} arrow>
      <span>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={disabled}
          sx={{
            px: 4,
            background: formSubmitting ? undefined : GRADIENT_CONFIRM
          }}
        >
          {formSubmitting ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
              Procesando...
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ mr: 1 }} />
              Confirmar Reserva
            </>
          )}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ReserveRoom;
